import CryptoJS from 'crypto-js'

/**
 * 安全AES加密工具类
 * 使用密钥混淆和分片存储，避免明文密钥暴露，同时保持与服务端的兼容性
 */
class SecureAES {
  constructor() {
    // 密钥片段 - 使用更长的随机密钥片段
    this.keyFragment1 = 'V2FsbGV0UEhfS2V5MjAyNFNlY3VyZUtleUZyYWdtZW50MQ==' // 更长的密钥片段
    this.ivFragment1 = 'Q3J5cHRvSVZfVmVjdG9yU2VjdXJlSW5pdGlhbGl6YXRpb24=' // 更长的IV片段

    // 多重异或密钥 - 使用更复杂的密钥
    this.xorKeys = {
      c1: 'U2VjdXJlWE9SMjAyNFNlY3VyaXR5S2V5Q29tcGxleA==', // 更长的异或密钥
      c2: '293821382938475629384756293847562938475629384756', // 更长的常量
      salt: 'V2FsbGV0U2FsdFNlY3VyaXR5U2FsdEZvckVuY3J5cHRpb24=' // 更长的盐值
    }

    // 缓存解析后的密钥
    this._cachedKey = null
    this._cachedIV = null
  }

  /**
   * 还原真实的RSA密钥
   * @param {string} keyType 密钥类型：'public' 或 'private'
   * @returns {string} 还原后的RSA密钥
   */
  restoreRSAKey(keyType = 'public') {
    try {
      let envKeyFragment = ''

      if (keyType === 'public') {
        envKeyFragment = process.env.VUE_APP_Z3C_ENCODED || ''
      } else if (keyType === 'private') {
        envKeyFragment = process.env.VUE_APP_W7D_ENCODED || ''
      }

      // 如果没有编码的密钥，尝试使用原始密钥（向后兼容）
      if (!envKeyFragment) {
        throw new Error('security error')
      }

      // 使用复杂异或解密还原密钥
      return this.complexXorDecrypt(envKeyFragment)
    } catch (error) {
      throw new Error('security error 3')
    }
  }

  /**
   * 还原真实的AES密钥和IV
   * 通过组合和解码分片来重建原始密钥，确保与服务端一致
   * @returns {Object} 包含key和iv的对象
   */
  restoreRealKeys() {
    // 如果已缓存，直接返回
    if (this._cachedKey && this._cachedIV) {
      return { key: this._cachedKey, iv: this._cachedIV }
    }

    try {
      // 从环境变量获取编码后的密钥片段（适配 bluepay-h5 项目）
      const envKeyFragment = process.env.VUE_APP_X1A_ENCODED || ''
      const envIVFragment = process.env.VUE_APP_Y9B_ENCODED || ''

      // 如果没有编码的密钥，尝试使用原始密钥（向后兼容）
      if (!envKeyFragment || !envIVFragment) {
        const originalKey = process.env.VUE_APP_X1A
        const originalIV = process.env.VUE_APP_Y9B

        if (originalKey && originalIV) {
          this._cachedKey = CryptoJS.enc.Utf8.parse(originalKey)
          this._cachedIV = CryptoJS.enc.Utf8.parse(originalIV)
          return { key: this._cachedKey, iv: this._cachedIV }
        }

        throw new Error('security error')
      }

      // 使用复杂异或解密还原密钥
      const realKey = this.complexXorDecrypt(envKeyFragment)
      const realIV = this.complexXorDecrypt(envIVFragment)

      // 解析为CryptoJS可用的格式
      this._cachedKey = CryptoJS.enc.Utf8.parse(realKey)
      this._cachedIV = CryptoJS.enc.Utf8.parse(realIV)
      return { key: this._cachedKey, iv: this._cachedIV }
    } catch (error) {
      throw new Error('security error 2')
    }
  }

  /**
   * Base64字符串转字节数组
   * @param {string} b64String Base64字符串
   * @returns {Uint8Array} 字节数组
   */
  b64ToBytes(b64String) {
    const binaryString = atob(b64String)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes
  }

  /**
   * 字节数组转Base64字符串
   * @param {Uint8Array} bytes 字节数组
   * @returns {string} Base64字符串
   */
  bytesToB64(bytes) {
    let binaryString = ''
    for (let i = 0; i < bytes.length; i++) {
      binaryString += String.fromCharCode(bytes[i])
    }
    return btoa(binaryString)
  }

  /**
   * 字节数组异或运算
   * @param {Uint8Array} bytes1 字节数组1
   * @param {Uint8Array} bytes2 字节数组2
   * @returns {Uint8Array} 异或结果
   */
  xorBytes(bytes1, bytes2) {
    const maxLength = Math.max(bytes1.length, bytes2.length)
    const result = new Uint8Array(maxLength)

    for (let i = 0; i < maxLength; i++) {
      const b1 = i < bytes1.length ? bytes1[i] : 0
      const b2 = i < bytes2.length ? bytes2[i] : 0
      result[i] = b1 ^ b2
    }

    return result
  }

  /**
   * 扩展密钥到指定长度
   * @param {Uint8Array} key 原始密钥
   * @param {number} targetLength 目标长度
   * @returns {Uint8Array} 扩展后的密钥
   */
  expandKey(key, targetLength) {
    if (key.length >= targetLength) {
      return key.slice(0, targetLength)
    }

    const expanded = new Uint8Array(targetLength)
    let keyIndex = 0

    for (let i = 0; i < targetLength; i++) {
      // 使用密钥循环填充，并添加位置相关的变换
      expanded[i] = key[keyIndex] ^ i % 256
      keyIndex = (keyIndex + 1) % key.length
    }

    return expanded
  }

  /**
   * 生成随机填充
   * @param {number} length 填充长度
   * @returns {Uint8Array} 随机填充字节
   */
  generateRandomPadding(length) {
    const padding = new Uint8Array(length)
    for (let i = 0; i < length; i++) {
      // 使用时间戳和位置生成伪随机数
      padding[i] = (Date.now() + i * 7) % 256
    }
    return padding
  }

  /**
   * 复杂异或解密 - 改进的多重异或方案
   * @param {string} encryptedB64 加密后的Base64字符串
   * @returns {string} 解密后的原始字符串
   */
  complexXorDecrypt(encryptedB64) {
    try {
      // 获取各个组件的字节数组
      const p1 = this.b64ToBytes(this.keyFragment1) // 代码中的片段1
      const p2 = this.b64ToBytes(this.ivFragment1) // 代码中的片段2
      const p3 = this.b64ToBytes(this.xorKeys.c1) // 异或密钥c1
      const p4 = this.b64ToBytes(btoa(this.xorKeys.c2)) // 常量c2转Base64后再转字节
      const encrypted = this.b64ToBytes(encryptedB64) // 加密的数据

      // 扩展密钥到加密数据的长度
      const expandedP1 = this.expandKey(p1, encrypted.length)
      const expandedP2 = this.expandKey(p2, encrypted.length)
      const expandedP3 = this.expandKey(p3, encrypted.length)
      const expandedP4 = this.expandKey(p4, encrypted.length)

      // 多重异或解密：encrypted XOR p1 XOR p2 XOR p3 XOR p4
      let tmp = this.xorBytes(encrypted, expandedP1)
      tmp = this.xorBytes(tmp, expandedP2)
      tmp = this.xorBytes(tmp, expandedP3)
      tmp = this.xorBytes(tmp, expandedP4)

      // 转回字符串
      let result = ''
      for (let i = 0; i < tmp.length; i++) {
        result += String.fromCharCode(tmp[i])
      }

      return result
    } catch (error) {
      console.error('复杂异或解密失败:', error)
      throw new Error('解密失败')
    }
  }

  /**
   * 复杂异或加密 - 改进的加密方案
   * @param {string} plaintext 原始字符串
   * @returns {string} 加密后的Base64字符串
   */
  complexXorEncrypt(plaintext) {
    try {
      // 将明文转为字节数组
      const plaintextBytes = new Uint8Array(plaintext.length)
      for (let i = 0; i < plaintext.length; i++) {
        plaintextBytes[i] = plaintext.charCodeAt(i)
      }

      // 获取各个组件的字节数组
      const p1 = this.b64ToBytes(this.keyFragment1)
      const p2 = this.b64ToBytes(this.ivFragment1)
      const p3 = this.b64ToBytes(this.xorKeys.c1)
      const p4 = this.b64ToBytes(btoa(this.xorKeys.c2))

      // 扩展密钥到明文长度
      const expandedP1 = this.expandKey(p1, plaintextBytes.length)
      const expandedP2 = this.expandKey(p2, plaintextBytes.length)
      const expandedP3 = this.expandKey(p3, plaintextBytes.length)
      const expandedP4 = this.expandKey(p4, plaintextBytes.length)

      // 多重异或加密：plaintext XOR p1 XOR p2 XOR p3 XOR p4
      let tmp = this.xorBytes(plaintextBytes, expandedP1)
      tmp = this.xorBytes(tmp, expandedP2)
      tmp = this.xorBytes(tmp, expandedP3)
      tmp = this.xorBytes(tmp, expandedP4)

      // 转为Base64
      return this.bytesToB64(tmp)
    } catch (error) {
      console.error('复杂异或加密失败:', error)
      throw new Error('加密失败')
    }
  }

  /**
   * 安全AES加密 - 使用还原后的真实密钥
   * @param {string} plaintext 待加密的明文
   * @returns {string} 加密后的Base64字符串
   */
  encrypt(plaintext = '') {
    if (!plaintext) return ''

    try {
      const { key, iv } = this.restoreRealKeys()

      const encrypted = CryptoJS.AES.encrypt(plaintext, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      })

      return CryptoJS.enc.Base64.stringify(encrypted.ciphertext)
    } catch (error) {
      console.error('AES加密失败:', error)
      throw new Error('加密失败')
    }
  }

  /**
   * 安全AES解密 - 使用还原后的真实密钥
   * @param {string} ciphertext 待解密的密文
   * @returns {string} 解密后的明文
   */
  decrypt(ciphertext = '') {
    if (!ciphertext) return ''

    try {
      const { key, iv } = this.restoreRealKeys()

      const decrypted = CryptoJS.AES.decrypt(ciphertext, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      })

      return decrypted.toString(CryptoJS.enc.Utf8)
    } catch (error) {
      console.error('AES解密失败:', error)
      return ''
    }
  }

  /**
   * 清除密钥缓存
   */
  clearKeyCache() {
    this._cachedKey = null
    this._cachedIV = null
  }
}

// 创建单例实例
const secureAES = new SecureAES()

/**
 * 安全AES加密（兼容原接口）
 * @param {string} str 需要加密的字符串
 * @returns {string} 加密后的字符串
 */
export function cryptoJSAesEncrypt(str = '') {
  return secureAES.encrypt(str)
}

/**
 * 安全AES解密（兼容原接口）
 * @param {string} str 需要解密的字符串
 * @returns {string} 解密后的字符串
 */
export function cryptoJSAesDecrypt(str = '') {
  return secureAES.decrypt(str)
}

/**
 * 创建密钥编码工具函数（用于生成编码后的环境变量值）
 * 使用复杂异或加密方案
 * @param {string} originalKey 原始密钥
 * @returns {string} 编码后的Base64字符串
 */
export function encodeKeyForEnv(originalKey) {
  // 创建临时实例来使用复杂异或加密
  const tempAES = new SecureAES()
  return tempAES.complexXorEncrypt(originalKey)
}

/**
 * 安全获取RSA公钥
 * @returns {string} RSA公钥
 */
export function getSecureRSAPublicKey() {
  return secureAES.restoreRSAKey('public')
}

/**
 * 安全获取RSA私钥
 * @returns {string} RSA私钥
 */
export function getSecureRSAPrivateKey() {
  return secureAES.restoreRSAKey('private')
}

export default secureAES
